import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { Bell, Store, Wrench, CalendarDays, Users, ShieldCheck, MapPin, ArrowRight, Sparkles } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/36202135/pexels-photo-36202135.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const MARKET_IMG =
  "https://images.unsplash.com/photo-1734255026082-82fdc81991f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIwbG9jYWwlMjBtYXJrZXR8ZW58MHx8fHwxNzg1MDU0MzU5fDA&ixlib=rb-4.1.0&q=85";
const COMMUNITY_IMG =
  "https://images.pexels.com/photos/5638604/pexels-photo-5638604.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CHAT_IMG =
  "https://images.unsplash.com/photo-1491438590914-bc09fcaaf77a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxmcmllbmRseSUyMHBlb3BsZSUyMGNoYXR0aW5nJTIwY29tbXVuaXR5fGVufDB8fHx8MTc4NTA1NDM2MHww&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { to: "/feed", icon: Users, title: "Community Feed", desc: "Say habari, share tips, celebrate wins together." },
  { to: "/alerts", icon: Bell, title: "Safety & Utility Alerts", desc: "Know first about power, water, roads and security." },
  { to: "/directory", icon: Wrench, title: "Trusted Service Directory", desc: "Find vetted fundis, salons, dukas and more nearby." },
  { to: "/marketplace", icon: Store, title: "Neighborhood Marketplace", desc: "Buy, sell, or swap with neighbors — priced in KES." },
  { to: "/events", icon: CalendarDays, title: "Events & Meetings", desc: "Cleanup days, watch meets, football fun days." },
  { to: "/register", icon: ShieldCheck, title: "Verified Neighbors", desc: "Real people, real neighborhoods, real karibu." },
];

export default function Home() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    document.title = "Jirani.co — Karibu, your friendly Kenyan neighborhood platform";
    api.get("/meta/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(244,162,97,0.25),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(46,71,59,0.18),transparent_50%)]" />
        </div>

        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 pb-16 pt-10 sm:px-6 md:pt-16 lg:grid-cols-12 lg:gap-16 lg:px-8">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#2E473B]/15 bg-white/60 px-3 py-1 text-xs backdrop-blur">
              <Sparkles className="h-3.5 w-3.5 text-[#D95D39]" />
              <span className="jirani-eyebrow !text-[10px]">Karibu · Made in Nairobi</span>
            </div>
            <h1
              className="mt-5 text-4xl leading-none tracking-tight text-[#1A1A1A] sm:text-5xl lg:text-6xl"
              style={{ fontFamily: "Outfit", fontWeight: 800 }}
            >
              Your neighborhood,
              <br />
              <span className="text-[#D95D39]">closer than ever.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#4A4A4A] sm:text-lg">
              Jirani is a place where you can find out what is happening around you. Neighbourhood alerts, local businesses, a marketplace, power and water outages and alerts, events and many more
            </p>
            <p
              data-testid="hero-subtagline"
              className="mt-3 max-w-xl text-sm italic leading-relaxed text-[#D95D39] sm:text-base"
            >
              * Let's make order and sense out of the flood of scattered messages
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register">
                <Button
                  data-testid="hero-cta-join"
                  className="jirani-pill h-12 rounded-full bg-[#D95D39] px-7 text-base font-semibold text-white hover:bg-[#c25333]"
                >
                  Join your neighborhood <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/feed">
                <Button
                  data-testid="hero-cta-explore"
                  variant="outline"
                  className="jirani-pill h-12 rounded-full border-[#2E473B]/25 bg-white/70 px-7 text-base font-semibold text-[#2E473B] hover:bg-white"
                >
                  Peek at the feed
                </Button>
              </Link>
            </div>

            {stats && (
              <dl
                data-testid="hero-stats"
                className="mt-10 grid max-w-xl grid-cols-3 gap-6 border-t border-black/5 pt-6 text-left"
              >
                <div>
                  <dt className="text-xs uppercase tracking-widest text-[#2E473B]/70">Members</dt>
                  <dd className="mt-1 text-2xl font-semibold text-[#1A1A1A]" style={{ fontFamily: "Outfit" }}>
                    {stats.members}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-widest text-[#2E473B]/70">Listings</dt>
                  <dd className="mt-1 text-2xl font-semibold text-[#1A1A1A]" style={{ fontFamily: "Outfit" }}>
                    {stats.marketplace}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-widest text-[#2E473B]/70">Alerts</dt>
                  <dd className="mt-1 text-2xl font-semibold text-[#1A1A1A]" style={{ fontFamily: "Outfit" }}>
                    {stats.alerts}
                  </dd>
                </div>
              </dl>
            )}
          </div>

          <div className="relative lg:col-span-6">
            <div className="relative isolate">
              <div className="absolute -left-6 -top-6 h-32 w-32 rounded-full bg-[#F4A261]/40 blur-2xl" />
              <div className="absolute -bottom-8 -right-6 h-40 w-40 rounded-full bg-[#2E473B]/25 blur-2xl" />
              <div className="relative overflow-hidden rounded-[28px] border border-black/5 bg-white shadow-[0_30px_60px_-30px_rgba(46,71,59,0.35)]">
                <img
                  src={HERO_IMG}
                  alt="Nairobi neighborhood at sunset"
                  className="h-[380px] w-full object-cover md:h-[480px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <h2
              className="mt-2 text-3xl tracking-tight text-[#1A1A1A] sm:text-4xl"
              style={{ fontFamily: "Outfit", fontWeight: 700 }}
            >
              One place for your whole neighbourhood.
            </h2>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ to, icon: Icon, title, desc }, i) => (
            <Link
              key={to + i}
              to={to}
              data-testid={`feature-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
              className="jirani-card group relative flex flex-col rounded-2xl border border-black/5 bg-white p-6"
            >
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#F9F7F4] text-[#2E473B] ring-1 ring-black/5 group-hover:bg-[#D95D39] group-hover:text-white">
                <Icon className="h-5 w-5" />
              </div>
              <h3
                className="mt-5 text-lg text-[#1A1A1A]"
                style={{ fontFamily: "Outfit", fontWeight: 700 }}
              >
                {title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-[#4A4A4A]">{desc}</p>
              <ArrowRight className="mt-4 h-4 w-4 text-[#D95D39] transition-transform group-hover:translate-x-1" />
            </Link>
          ))}
        </div>
      </section>

      {/* SPLIT */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-2">
          <div className="relative overflow-hidden rounded-3xl bg-[#2E473B] text-white">
            <img
              src={COMMUNITY_IMG}
              alt="Community"
              className="absolute inset-0 h-full w-full object-cover opacity-30"
            />
            <div className="relative p-8 sm:p-12">
              <div className="jirani-eyebrow !text-[#F4A261]">Feel at home</div>
              <h3
                className="mt-3 text-3xl tracking-tight sm:text-4xl"
                style={{ fontFamily: "Outfit", fontWeight: 700 }}
              >
                Real neighbors,
                <br /> real conversations.
              </h3>
              <p className="mt-4 max-w-md text-white/80">
                Ask for tips, celebrate wins, or offer a helping hand — Jirani is where everyday warmth lives online.
              </p>
              <Link to="/feed">
                <Button
                  data-testid="split-feed-cta"
                  className="jirani-pill mt-6 rounded-full bg-[#D95D39] px-6 text-white hover:bg-[#c25333]"
                >
                  Open the feed
                </Button>
              </Link>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-3xl border border-black/5 bg-white">
            <img
              src={MARKET_IMG}
              alt="Local marketplace"
              className="h-64 w-full object-cover sm:h-72"
            />
            <div className="p-8 sm:p-10">
              <div className="jirani-eyebrow">Marketplace</div>
              <h3
                className="mt-2 text-2xl tracking-tight text-[#1A1A1A] sm:text-3xl"
                style={{ fontFamily: "Outfit", fontWeight: 700 }}
              >
                Buy & sell right next door.
              </h3>
              <p className="mt-3 text-[#4A4A4A]">
                Furniture, baby gear, home-baked bread, phones and more — all priced in KES from real neighbors.
              </p>
              <Link to="/marketplace">
                <Button
                  data-testid="split-market-cta"
                  variant="outline"
                  className="jirani-pill mt-5 rounded-full border-[#2E473B]/25 px-6 text-[#2E473B] hover:bg-[#2E473B]/5"
                >
                  Browse listings
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl border border-black/5 bg-[#F9F7F4] p-10 sm:p-14">
          <img
            src={CHAT_IMG}
            alt=""
            className="absolute right-0 top-0 hidden h-full w-1/2 object-cover opacity-40 md:block"
          />
          <div className="relative max-w-xl">
            <div className="jirani-eyebrow">Get started</div>
            <h3
              className="mt-2 text-3xl tracking-tight text-[#1A1A1A] sm:text-4xl"
              style={{ fontFamily: "Outfit", fontWeight: 700 }}
            >
              Ready to meet your neighbors?
            </h3>
            <p className="mt-3 text-[#4A4A4A]">
              Sign up in less than a minute and jump into the conversation. Karibu sana!
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/register">
                <Button
                  data-testid="footer-cta-signup"
                  className="jirani-pill h-12 rounded-full bg-[#D95D39] px-7 text-white hover:bg-[#c25333]"
                >
                  Create free account
                </Button>
              </Link>
              <Link to="/login">
                <Button
                  data-testid="footer-cta-login"
                  variant="outline"
                  className="jirani-pill h-12 rounded-full border-[#2E473B]/25 px-7 text-[#2E473B] hover:bg-white"
                >
                  I already have an account
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
